import { FaCheck } from 'react-icons/fa6';
import { IoMdCloseCircle } from 'react-icons/io';
import { FaEllipsisH } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { useChatHistory } from '@Context/ChatHistoryContext';
import { MoonLoader } from 'react-spinners';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';

interface Props {
  search: string;
  newTitle: string;
  setNewTitle: React.Dispatch<React.SetStateAction<string>>;
  setShowDelete: React.Dispatch<React.SetStateAction<boolean>>;
}

const ConversationHistory: React.FC<Props> = ({
  search,
  newTitle,
  setNewTitle,
  setShowDelete,
}) => {
  const { fetchChatHistories, state, dispatch, renameIndex, renameChat } =
    useChatHistory();
  const { chatHistories, currentChatId } = state;
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [existingChatNameIndex, setExistingChatNameIndex] = useState<
    number | null
  >(null);
  const { loaders } = useUtilityContext();

  useEffect(() => {
    fetchChatHistories();
  }, [fetchChatHistories]);

  useEffect(() => {
    setTimeout(() => {
      existingChatNameIndex !== null && setExistingChatNameIndex(null);
    }, 3000);
  }, [existingChatNameIndex]);

  const handleRenameClick = (index: number, currentTitle: string) => {
    setEditIndex(index);
    setNewTitle(currentTitle);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTitle(e.target.value);
  };

  const openMiniMenu = (index: number, id: string) => {
    if (editIndex !== index) {
      setEditIndex(null);
    }
    dispatch({
      type: 'SET_CURRENT_CHAT',
      payload: { id: id },
    });
  };

  const hideMiniMenu = (index: number) => {
    const menu = document.getElementById(`menu-${index}`);
    if (menu) {
      menu.classList.add('hidden');
    }
  };

  const showMiniMenu = (index: number) => {
    const menu = document.getElementById(`menu-${index}`);
    if (menu) {
      menu.classList.toggle('hidden');
    }
  };

  return (
    <>
      {loaders.chatHistory ? (
        <span className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-5">
          <MoonLoader size={100} color="#071D49" />
          <span className="animate-pulse text-sm font-semibold tracking-wide text-gray-600">
            Chat History Loading....
          </span>
        </span>
      ) : chatHistories.length > 0 ? (
        chatHistories
          .filter((chats) =>
            chats.title.toLowerCase().includes(search.toLowerCase())
          )
          .map((chat, index) => (
            <span
              onClick={() => openMiniMenu(index, chat.id)}
              onMouseLeave={() => hideMiniMenu(index)}
              key={index}
              className={`${
                currentChatId === chat.id &&
                (existingChatNameIndex === index
                  ? 'bg-brandError-200 text-brandError-700'
                  : 'bg-primary-700 text-white hover:bg-primary-700')
              } group relative flex w-full items-center justify-between rounded-md p-2 text-xs hover:cursor-pointer hover:bg-gray-300`}
            >
              {editIndex === index ? (
                <div className="flex w-full flex-row items-center justify-center gap-2">
                  <input
                    value={newTitle}
                    maxLength={40}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 p-1 text-black"
                    autoFocus
                  />
                  {renameIndex == index ? (
                    <div>
                      <MoonLoader size={20} color="#FFFFFF" />
                    </div>
                  ) : (
                    <>
                      <button
                        disabled={newTitle === chat.title || newTitle === ''}
                        onClick={() => {
                          chatHistories.find((c) => c.title === newTitle)
                            ? setExistingChatNameIndex(index)
                            : renameChat(chat.id, newTitle);
                          setEditIndex(null);
                        }}
                      >
                        <FaCheck
                          size={25}
                          color={`${
                            newTitle === chat.title || newTitle === ''
                              ? 'grey'
                              : 'green'
                          }`}
                        />
                      </button>
                      <button onClick={() => setEditIndex(null)}>
                        <IoMdCloseCircle size={25} color="red" />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <p>
                    {existingChatNameIndex === index
                      ? 'Chat Name Already Exists'
                      : chat.title}
                  </p>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      showMiniMenu(index);
                    }}
                  >
                    <FaEllipsisH
                      size={15}
                      className={`${
                        currentChatId !== chat.id && 'hidden'
                      } group-hover:block`}
                    />
                  </span>
                </>
              )}

              <div
                id={`menu-${index}`}
                onMouseLeave={() => {
                  hideMiniMenu(index);
                }}
                className="absolute right-0 top-6 z-10 hidden rounded-md border border-gray-300 bg-white text-black shadow-lg"
              >
                <div
                  className="cursor-pointer p-2 hover:bg-gray-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRenameClick(index, chat.title);
                    hideMiniMenu(index);
                  }}
                >
                  Rename
                </div>
                <div
                  className="cursor-pointer p-2 hover:bg-gray-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewTitle(chat.title);
                    dispatch({
                      type: 'SET_CURRENT_CHAT',
                      payload: { id: chat.id },
                    });
                    setShowDelete(true);
                  }}
                >
                  Delete
                </div>
              </div>
            </span>
          ))
      ) : (
        <span className="group flex w-full items-center justify-between rounded-md p-2 text-xs hover:cursor-pointer hover:bg-gray-200">
          <p>No Conversation History</p>
        </span>
      )}
    </>
  );
};

export default ConversationHistory;
